import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { fmtDate, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { crewsMessages } from '@/i18n/messages/crews'
import { Badge } from './Badge'
import { needsAttention, type CrewListItem } from './types'

const INPUT =
  'w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950'

const FIELDS = ['name', 'code', 'company', 'leader', 'bauleiter'] as const

/**
 * Edit a crew via a modal — explicitly, not by clicking a cell. Shows every list field;
 * identity (name, code, company, leader, Bauleiter) is saved as aufmass.crew_override,
 * layered over AU on read. Attendance figures stay derived from check-ins. AU is never written.
 */
export function EditCrewDialog({ crew, onClose }: { crew: CrewListItem; onClose: () => void }) {
  const t = useT(crewsMessages)
  const tc = useT(commonMessages)
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const s = (v: string | null | undefined) => v ?? ''
  const [f, setF] = useState<Record<(typeof FIELDS)[number], string>>({
    name: s(crew.name),
    code: s(crew.code),
    company: s(crew.company),
    leader: s(crew.leader),
    bauleiter: s(crew.bauleiter),
  })
  const original = {
    name: s(crew.name),
    code: s(crew.code),
    company: s(crew.company),
    leader: s(crew.leader),
    bauleiter: s(crew.bauleiter),
  }
  const set = (k: (typeof FIELDS)[number]) => (v: string) => setF((prev) => ({ ...prev, [k]: v }))

  const save = useMutation({
    mutationFn: async () => {
      const changed = FIELDS.filter((k) => f[k] !== original[k])
      await Promise.all(changed.map((k) =>
        api.put(`/api/crews/${crew.crewId}/overrides/${k}`, { value: f[k] === '' ? null : f[k] })))
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crews'] })
      void qc.invalidateQueries({ queryKey: ['crew', crew.crewId] })
      onClose()
    },
    onError: (e) =>
      setError(e instanceof ApiError ? `${e.message}${e.detail ? ` — ${e.detail}` : ''}` : t('couldNotSave')),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" onClick={onClose}
    >
      <div
        className="flex max-h-[min(88vh,100dvh-2rem)] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-5 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold">{t('editCrew')}</h2>
            <p className="mt-0.5 text-sm text-neutral-500">{crew.name ?? '—'}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={tc('close')}
                  className="grid size-10 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200">✕</button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <Section title={t('identity')}>
            <Field label={t('colCrew')}><input className={INPUT} value={f.name} onChange={(e) => set('name')(e.target.value)} /></Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t('colCode')}><input className={INPUT} value={f.code} onChange={(e) => set('code')(e.target.value)} /></Field>
              <Field label={t('colCompany')}><input className={INPUT} value={f.company} onChange={(e) => set('company')(e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t('colLeader')}><input className={INPUT} value={f.leader} onChange={(e) => set('leader')(e.target.value)} /></Field>
              <Field label={t('colBauleiter')}><input className={INPUT} value={f.bauleiter} onChange={(e) => set('bauleiter')(e.target.value)} /></Field>
            </div>
          </Section>

          <Section title={t('attendance')}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Dt>{t('colHeadcount')}</Dt>
              <Dd>{crew.members} / {crew.roster}</Dd>
              <Dt>{t('colLastCheckIn')}</Dt>
              <Dd>{crew.lastCheckIn ? fmtDate(crew.lastCheckIn) : t('neverCheckedIn')}</Dd>
              <Dt>{t('colChecks')}</Dt>
              <Dd><CrewStatus crew={crew} /></Dd>
            </dl>
          </Section>

          <p className="text-xs text-neutral-400">{t('editNote')}</p>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-neutral-200 p-5 dark:border-neutral-800">
          {error
            ? <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p>
            : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
                    className="rounded-lg px-3 py-1.5 text-sm text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800">{tc('cancel')}</button>
            <button type="button" onClick={() => { setError(null); save.mutate() }} disabled={save.isPending}
                    className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60">
              {save.isPending ? t('saving') : tc('save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CrewStatus({ crew }: { crew: CrewListItem }) {
  const t = useT(crewsMessages)
  if (!crew.active) return <Badge tone="grey">{t('inactive')}</Badge>
  if (!needsAttention(crew)) return <Badge tone="green">{t('active')}</Badge>
  return (
    <div className="flex flex-wrap gap-1">
      {crew.leaderNotCheckedIn && <Badge tone="red">{t('warnLeaderNotCheckedIn')}</Badge>}
      {crew.notOnRoster > 0 && <Badge tone="amber">{t('warnNotOnRoster', { count: crew.notOnRoster })}</Badge>}
      {crew.rosterLeft > 0 && <Badge tone="amber">{t('warnRosterLeft', { count: crew.rosterLeft })}</Badge>}
      {crew.bauleiterOutdatedInAu && <Badge tone="grey">{t('colBauleiter')} ⚠</Badge>}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

function Dt({ children }: { children: ReactNode }) {
  return <dt className="text-xs font-medium text-neutral-500">{children}</dt>
}

function Dd({ children }: { children: ReactNode }) {
  return <dd className="text-neutral-800 dark:text-neutral-200">{children}</dd>
}
